import React, { useState, useEffect } from "react";
import { gql, useQuery, useLazyQuery } from "@apollo/client";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { DataTableManual } from "@/components/shared/data-table-manual/data-table-manual";
import { ColumnDef } from "@tanstack/react-table";
import SofaBlueprint from "@/configurator/SofaBlueprint";
import SofaFormEditForm from "./SofaFormEditForm";
import useOpenDrawer from "@/hooks/useOpenDrawer";
import { GET_ADDITIONAL_COMPONENTS_BY_CODES } from "@/components/queries/additionalComponentQueries";
import { SheetScrollableContent } from "@/components/ui/sheet-scrollable-content";

const GET_SOFA_FORMS_WITH_COMPONENT = gql`
  query GetSofaFormsWithComponent($productId: Int!) {
    findUniqueAdvancedProduct(where: { id: $productId }) {
      id
      sofa_forms {
        id
        code
        enabled
        left_and_right
        name
        type
        metadata
        dimensions {
          width
          length
          armrest_width
          backrest_width
          mattress_width
          mattress_length
          corner_part_length
          angle
          seat_height
          extendable_part_length
          number_of_big_pillows
          number_of_small_pillows
          spread_of_big_pillows
          spread_of_small_pillows
          size_of_big_pillow
          size_of_pillow
        }
        package_dimensions {
          id
          volume
          name
        }
      }
    }
  }
`;

interface PackageDimension {
  id: string;
  volume: number;
  name: string;
}

interface SofaForm {
  id: string;
  code: string;
  enabled: boolean;
  left_and_right: boolean;
  name: string;
  type: string;
  metadata: any;
  dimensions: {
    width?: number | null;
    length?: number | null;
    armrest_width?: number | null;
    backrest_width?: number | null;
    mattress_width?: number | null;
    mattress_length?: number | null;
    corner_part_length?: number | null;
    angle?: number | null;
    seat_height?: number | null;
    extendable_part_length?: number | null;
    number_of_big_pillows?: number | null;
    number_of_small_pillows?: number | null;
    spread_of_big_pillows?: number | null;
    spread_of_small_pillows?: number | null;
    size_of_big_pillow?: number | null;
    size_of_pillow?: number | null;
  };
  package_dimensions: PackageDimension[];
}

interface ModuleCompatibilityDrawerProps {
  data: {
    advancedProductId: number;
    componentCode: string;
    componentName: string;
  };
  onRequestClose?: () => void;
}

export default function ModuleCompatibilityDrawer({
  data,
  onRequestClose,
}: ModuleCompatibilityDrawerProps) {
  const { language } = useLanguage();
  const [compatibleModules, setCompatibleModules] = useState<SofaForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [componentDetails, setComponentDetails] = useState<any>(null);
  const openSofaFormEdit = useOpenDrawer(SofaFormEditForm);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [order, setOrder] = useState([]);

  const [loadComponentDetails, { data: componentData }] = useLazyQuery(
    GET_ADDITIONAL_COMPONENTS_BY_CODES,
    {
      variables: {
        codes: [data.componentCode],
      },
      fetchPolicy: "network-only",
    }
  );

  const { data: sofaFormsData, loading } = useQuery(
    GET_SOFA_FORMS_WITH_COMPONENT,
    {
      variables: {
        productId: data.advancedProductId,
      },
      fetchPolicy: "network-only",
      onCompleted: () => {
        setIsLoading(false);
      },
    }
  );

  const columns: ColumnDef<SofaForm>[] = [
    {
      accessorKey: "blueprint",
      header: "Blueprint",
      cell: ({ row }) => (
        <SofaBlueprint
          id={parseInt(row.original.id)}
          dimensions={row.original.dimensions}
          type={row.original.type}
          containerHeight="60px"
          containerWidth="60px"
        />
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => (
        <div
          className={`space-x-2 ${!row.original.enabled ? "opacity-30" : ""}`}
        >
          <Badge variant="outline">{row.original.type}</Badge>
          {row.original.left_and_right && (
            <Badge variant="outline">Left & Right</Badge>
          )}
          <span className="text-muted-foreground">{row.original.code}</span>
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openSofaFormEdit(row.original)}
        >
          <Edit />
        </Button>
      ),
    },
  ];

  // Load component details when component mounts
  useEffect(() => {
    loadComponentDetails();
  }, [loadComponentDetails]);

  // Update component details when data is loaded
  useEffect(() => {
    if (componentData?.findManyAdditionalComponent?.length > 0) {
      setComponentDetails(componentData.findManyAdditionalComponent[0]);
    }
  }, [componentData]);

  useEffect(() => {
    if (sofaFormsData?.findUniqueAdvancedProduct?.sofa_forms) {
      const sofaForms = sofaFormsData.findUniqueAdvancedProduct.sofa_forms;
      const compatibleModulesList: SofaForm[] = [];

      // Process each sofa form's metadata
      sofaForms.forEach((form: any) => {
        try {
          const metadata =
            typeof form.metadata === "string"
              ? JSON.parse(form.metadata)
              : form.metadata;

          // Check if armrestsConditions exists in metadata and includes our component code
          if (
            metadata?.armrestsConditions &&
            Array.isArray(metadata.armrestsConditions) &&
            metadata.armrestsConditions.includes(data.componentCode)
          ) {
            compatibleModulesList.push(form);
          }
        } catch (error) {
          console.error("Error processing sofa form metadata:", error);
        }
      });

      setCompatibleModules(compatibleModulesList);
    }
  }, [sofaFormsData, data.componentCode]);

  if (isLoading || loading) {
    return (
      <SheetContent>
        <div className="flex items-center justify-center h-full">
          Loading...
        </div>
      </SheetContent>
    );
  }

  return (
    <SheetContent className="sheet-content">
      <SheetHeader>
        <SheetTitle>
          Modules with Specific Compatibility Rules for {data.componentName}
        </SheetTitle>
      </SheetHeader>
      <SheetScrollableContent>
        <div className="space-y-4 max-w-3xl mx-auto py-5">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-md mb-4">
            {componentDetails?.image?.src_thumbnail ? (
              <img
                src={
                  componentDetails.image.src_md ||
                  componentDetails.image.src_thumbnail
                }
                alt={
                  componentDetails.additional_component_profiles[0]?.name ||
                  data.componentName
                }
                className="w-16 h-16 object-cover rounded-md"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center text-gray-400">
                <span className="text-xs">No Image</span>
              </div>
            )}
            <div>
              <h3 className="font-medium">{data.componentName}</h3>
              <p className="text-sm text-gray-500">
                Code: {data.componentCode}
              </p>
            </div>
          </div>
          <div className="bg-blue-50 p-3 rounded-md mb-4 text-sm">
            <p className="font-medium mb-1">Module Compatibility</p>
            <p className="text-gray-700">
              Listed modules have specific compatibility rules for the{" "}
              {data.componentName} component. Modules not shown here either have
              no restrictions or don't use armrests.
            </p>
          </div>
          {compatibleModules.length === 0 ? (
            <p className="text-center text-gray-500">
              No compatible modules found
            </p>
          ) : (
            <DataTableManual
              columns={columns}
              data={compatibleModules}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              rowCount={compatibleModules.length}
              onPaginationChange={setPagination}
              onSortingChange={setOrder}
              onColumnFiltersChange={() => {}}
            />
          )}
        </div>
      </SheetScrollableContent>
    </SheetContent>
  );
}
